import {
    AccountInfo,
    AuthenticationResult,
    Configuration,
    EndSessionRequest,
    InteractionRequiredAuthError,
    PopupRequest,
    PublicClientApplication,
    RedirectRequest,
} from "@azure/msal-browser";

import MSAL_CONFIG from "./authConfig";

export class AzureAuthenticationContext {
    private myMSALObj: PublicClientApplication;
    private account?: AccountInfo;
    private loginRedirectRequest?: RedirectRequest;
    private loginRequest?: PopupRequest;

    public isAuthenticationConfigured = false;

    constructor() {
        // @ts-ignore
        this.account = null;
        var msalConfig = new MSAL_CONFIG();
        var config = msalConfig.getConfig();

        this.myMSALObj = new PublicClientApplication(
            config
        );

        this.setRequestObjects(config);

        if (config?.auth?.clientId) {
            this.isAuthenticationConfigured = true;
        }
    }

    private getApiScopes(): string[] {
        // The backend API is exposed on its own dedicated app registration
        // (Expose an API -> access_as_user) - a different app registration
        // from the one used for login (see authConfig.ts).
        var adApiApplicationClientId = localStorage.getItem('adApiApplicationClientId');
        return adApiApplicationClientId ? [`api://${adApiApplicationClientId}/access_as_user`] : [];
    }

    private setRequestObjects(config: Configuration): void {
        this.loginRequest = {
            scopes: this.getApiScopes(),
            prompt: "none",
        };

        this.loginRedirectRequest = {
            ...this.loginRequest,
            authority: config.auth.authority,
            redirectStartPage: window.location.href,
        };
    }

    async acquireApiToken(): Promise<string> {
        const account = this.account ?? this.getAccount();
        if (!account) {
            throw new Error("No authenticated account available to acquire an API token for.");
        }

        const scopes = this.getApiScopes();

        try {
            const result = await this.myMSALObj.acquireTokenSilent({ scopes, account });
            return result.accessToken;
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                const result = await this.myMSALObj.acquireTokenPopup({ scopes });
                return result.accessToken;
            }
            throw err;
        }
    }

    login(signInType: string, setUser: any, setLoginButton: any): void {
        if (signInType === "loginPopup") {
            this.myMSALObj
                .loginPopup(this.loginRequest)
                .then((resp: AuthenticationResult) => {
                    this.handleResponse(resp, setUser);
                })
                .catch((err) => {
                    // eslint-disable-next-line
                    var errorMsg = (err as Error);

                    if (errorMsg.name === "InteractionRequiredAuthError") {
                        this.loginRequest = {
                            scopes: this.getApiScopes(),
                            prompt: "select_account"
                        };
                        this.myMSALObj.loginPopup(this.loginRequest)
                            .then((resp: AuthenticationResult) => {
                                this.handleResponse(resp, setUser);
                            })
                            .catch((err) => {
                                setLoginButton(false)
                                // eslint-disable-next-line
                                //console.error(err);
                            });
                    }
                });
        } else if (signInType === "loginRedirect") {
            this.myMSALObj.loginRedirect(this.loginRedirectRequest);
        }
    }

    logout(account: AccountInfo): void {
        const logOutRequest: EndSessionRequest = {
            account,
        };

        this.myMSALObj.logout(logOutRequest);
    }
    handleResponse(response: AuthenticationResult, incomingFunction: any) {
        if (response !== null && response.account !== null) {
            this.account = response.account;
        } else {
            this.account = this.getAccount();
        }

        if (this.account) {
            incomingFunction(this.account);
        }
    }
    getAccount(): AccountInfo | undefined {
        const currentAccounts = this.myMSALObj.getAllAccounts();

        if (currentAccounts === null) {
            return undefined;
        }

        if (currentAccounts.length > 1) {
            return currentAccounts[0];
        } else if (currentAccounts.length === 1) {
            return currentAccounts[0];
        }
    }
}

export default AzureAuthenticationContext;